import { useState } from 'react'
import { Search, AlertTriangle, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import i18n from '../../i18n'
import { localizeApiDetail } from '../../i18n/apiError'
import { formatNorwegianDate } from '../../utils/dateUtils'
import HeftelseKort, { formaterBelop } from './HeftelseKort'
import KvoteBadge from './KvoteBadge'
import {
  heftelseOppslag,
  parseSokeliste,
  type Feil,
  type HeftelseTreff,
  type Kvote,
} from './oppslagApi'
import type { OppslagKey } from './detaljFelter'

const MAKS_BATCH = 100

const FEILTEKST: Record<string, OppslagKey> = {
  TOMT_SOK: 'feilkoder.tomtSok',
  IKKE_AUTORISERT: 'feilkoder.ikkeAutorisert',
  FOR_MANGE_FORESPORSLER: 'feilkoder.forMangeForesporsler',
}

/** One row per lien, flattened for export. A vehicle with no liens keeps a row
 *  saying so — the absence is the answer, and dropping it would make "no
 *  liens" indistinguishable from "never looked up". */
const KOLONNER: { key: string; labelKey: OppslagKey }[] = [
  { key: 'kjennemerke', labelKey: 'kolonner.kjennemerke' },
  { key: 'type', labelKey: 'heftelser.kolonner.heftelse' },
  { key: 'kategori', labelKey: 'heftelser.kolonner.kategori' },
  { key: 'status', labelKey: 'kolonner.status' },
  { key: 'tinglyst', labelKey: 'heftelser.kolonner.tinglyst' },
  { key: 'belop', labelKey: 'heftelser.kolonner.belop' },
  { key: 'rettighetshaver', labelKey: 'heftelser.kolonner.rettighetshaver' },
  { key: 'rettighetshaver_orgnr', labelKey: 'heftelser.kolonner.rettighetshaverOrgnr' },
  { key: 'forpliktede', labelKey: 'heftelser.kolonner.forpliktet' },
  { key: 'antall_kjoretoy', labelKey: 'heftelser.kolonner.antallKjoretoy' },
  { key: 'andre_kjoretoy', labelKey: 'heftelser.kolonner.ovrigeKjoretoy' },
]

function tilRader(treff: HeftelseTreff[]) {
  return treff.flatMap((t) =>
    t.heftelser.length === 0
      ? [{ kjennemerke: t.kjennemerke ?? t.sokt, type: i18n.t('heftelser.noneExport', { ns: 'oppslag' }) }]
      : t.heftelser.map((h) => ({
          kjennemerke: t.kjennemerke ?? t.sokt,
          type: h.type ?? '',
          kategori: h.kategori,
          status: h.status ?? '',
          tinglyst: h.tinglyst ? formatNorwegianDate(h.tinglyst) : '',
          belop: formaterBelop(h.belop, h.valuta),
          rettighetshaver: h.rettighetshaver?.navn ?? '',
          rettighetshaver_orgnr: h.rettighetshaver?.organisasjonsnummer ?? '',
          forpliktede: h.forpliktede.map((f) => f.navn).filter(Boolean).join('; '),
          antall_kjoretoy: h.antall_kjoretoy,
          andre_kjoretoy: h.andre_kjoretoy.join(', '),
        })),
  )
}

export default function HeftelserTab() {
  const { t } = useTranslation('oppslag')
  const [raa, setRaa] = useState('')
  const [laster, setLaster] = useState(false)
  const [treff, setTreff] = useState<HeftelseTreff[]>([])
  const [feil, setFeil] = useState<Feil[]>([])
  const [kvote, setKvote] = useState<Kvote | null>(null)
  const [apiFeil, setApiFeil] = useState<string | null>(null)

  const sok = parseSokeliste(raa)
  const forMange = sok.length > MAKS_BATCH

  async function utfor() {
    if (!sok.length || forMange) return
    setLaster(true)
    setApiFeil(null)
    try {
      const svar = await heftelseOppslag(sok)
      setTreff(svar.treff)
      setFeil(svar.feil)
      setKvote(svar.kvote)
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setApiFeil(
        typeof d === 'string'
          ? (localizeApiDetail(d) ?? t('felles.lookupFailed'))
          : (localizeApiDetail(d?.melding) ?? t('felles.lookupFailed')),
      )
    } finally {
      setLaster(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('heftelser.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('heftelser.intro', { max: MAKS_BATCH })}
            </p>
          </div>
          <KvoteBadge kvote={kvote} api="brreg" />
        </div>

        <textarea
          data-testid="heftelser-input"
          value={raa}
          onChange={(e) => setRaa(e.target.value)}
          rows={5}
          placeholder={'AE47056\nST14167'}
          className="w-full rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 text-sm font-mono"
        />

        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-testid="heftelser-sok"
            onClick={utfor}
            disabled={laster || !sok.length || forMange}
            className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {laster ? t('felles.searching') : t('felles.searchCount', { n: sok.length })}
          </button>
          {forMange && (
            <span
              data-testid="heftelser-formange"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {t('felles.tooMany', { n: sok.length, max: MAKS_BATCH })}
            </span>
          )}
          {!!sok.length && !forMange && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('heftelser.cost', { n: sok.length })}
            </span>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('heftelser.onlyPlate')}
        </p>
      </div>

      {apiFeil && (
        <div
          data-testid="heftelser-apifeil"
          className="card p-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{apiFeil}</span>
        </div>
      )}

      {!!treff.length && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {t('heftelser.summary', {
                kjoretoy: treff.length,
                aktive: treff.reduce((n, tr) => n + tr.antall_aktive, 0),
              })}
            </span>
            <div data-testid="heftelser-eksport">
              <ExportButtons
                data={tilRader(treff)}
                columns={KOLONNER.map((k) => ({ key: k.key, label: t(k.labelKey) }))}
                metadata={{
                  title: t('heftelser.exportTitle'),
                  filters: [t('heftelser.exportFilter', { n: sok.length })],
                  explanationText: t('heftelser.exportExplanation'),
                }}
              />
            </div>
          </div>

          {treff.map((tr) => (
            <div
              key={tr.sokt}
              className="border-t border-gray-200 dark:border-gray-700 pt-3 first:border-0 first:pt-0"
              data-testid={`heftelser-kjoretoy-${tr.sokt.replace(/\s/g, '')}`}
            >
              <div className="font-mono font-semibold text-gray-900 dark:text-white mb-2">
                {tr.kjennemerke ?? tr.sokt}
              </div>
              {tr.heftelser.length === 0 ? (
                // Said out loud. An empty space would read as "not checked".
                <div
                  className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400"
                  data-testid="heftelser-ingen"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {t('heftelser.noneRegistered')}
                </div>
              ) : (
                <div className="space-y-2">
                  {tr.heftelser.map((h, i) => (
                    <HeftelseKort key={h.dokumentnummer ?? i} heftelse={h} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!!feil.length && (
        <div className="card p-4" data-testid="heftelser-feil">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            {t('heftelser.couldNotLookUp', { n: feil.length })}
          </h3>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            {feil.map((f, i) => (
              <li key={i}>
                <span className="font-mono">{f.sokt || t('felles.empty')}</span> —{' '}
                {FEILTEKST[f.kode] ? t(FEILTEKST[f.kode]) : f.kode}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
